import { Box, Tooltip, IconButton } from '@material-ui/core'
import React, { useRef, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { IconWrapper, StatusDeviceIndicator } from 'shared/ui'
import {
  Button,
  DeviceModel,
  DropList,
  DropListItem,
  formatDeviceId,
  Typography,
} from 'sputnik-ui'
import { ReactComponent as MoreOptions } from 'assets/MoreOptions.svg'
import styled from 'styled-components'
import { Camera, Intercom, IconColors } from 'sputnik-icons'
import { useTranslation } from 'react-i18next'
import { keyDownWithClick } from 'utils/helpers'
import { DeviceServiceIcons } from 'components/atoms/DeviceServiceFormatted'
import { isEltex, isThirdParty } from 'utils/thirdPartyIntercomKinds'
import { ReactComponent as EditOutline } from 'assets/EditOutline.svg'
import ThirdPartyEditIcon from 'components/molecules/ThirdPartyEditIcon'
import {
  CameraNoticeField,
  CAMERA_NOTICE_MAX_LENGTH,
} from 'features/Devices/CameraNoticeField'
import { updateCameraNotice } from 'features/Devices/devicesSlice'
import { DeviceServiceNoteModal } from 'features/DeviceServiceNote'
import { showErrorMessage, showInfoMessage } from 'store/ui/actions'

const DeviceCard = styled.div`
  width: 100%;
  /* height: 54px; */
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  border-bottom: 1px solid ${(p) => p.theme.palette.stroke.line};
  padding-right: 24px;
  margin-top: 12px;
  padding-bottom: 7px;
  padding-top: 7px;
  /* &:first-child {
    margin-top: 0px;
  } */
  height: fit-content;
`

const CameraNoticePreview = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
`

const CameraNoticeEditor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-basis: 100%;
  margin: 14px 0 8px 56px;

  > div:first-child {
    flex: 1;
  }
`

const CameraNoticeActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const AddressDeviceElement = ({
  device,
  makeMain,
  goToDeviceAccess,
  disconnectDevice,
  goToDeviceZone,
  selectArchiveDevice,
  selectArchiveDepth,
  goToDevicePage,
  openCameraModal,
  isCamera,
  selected,
  openThirdPartyEdit,
  ...props
}) => {
  const anchorEl = useRef()
  const dispatch = useDispatch()
  const { t } = useTranslation('devices')
  const [isMenuOpen, setMenuOpen] = useState(false)
  // У контроллера Eltex нет камеры — архив и его глубина не применимы.
  const isEltexDevice = isEltex(device?.model)
  const [isCameraNoticeEditing, setCameraNoticeEditing] = useState(false)
  const [isCameraNoticeSaving, setCameraNoticeSaving] = useState(false)
  const [cameraNotice, setCameraNotice] = useState(device.notice || '')
  const [isServiceNoteOpen, setServiceNoteOpen] = useState(false)
  const handleToggleMenu = () => {
    setMenuOpen(!isMenuOpen)
  }
  const archive_icons = DeviceServiceIcons({
    color: IconColors.blue,
    style: {
      fontSize: '1.25rem',
      width: 24,
      height: 24,
    },
  })

  const [deviceServices, setDeviceServices] = useState([])
  useEffect(() => {
    const servicesAll = []
    servicesAll.push('bitrate_1024')
    if (device?.services) {
      setDeviceServices([...device.services, ...servicesAll])
    } else {
      setDeviceServices(['archive_0', ...servicesAll])
    }
  }, [setDeviceServices, device])

  useEffect(() => {
    setCameraNotice(device.notice || '')
  }, [device.notice])

  const handleClick = (event) => {
    keyDownWithClick(event, () =>
      isCamera ? openCameraModal(device) : goToDevicePage(device.uuid)
    )
  }

  const handleEditServiceNote = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Модалки сторонних устройств подключены только на странице подъезда;
    // на странице дома им, как и своим устройствам, достаётся модалка заметки.
    if (
      openThirdPartyEdit &&
      isThirdParty(device?.serial_number, device?.type, device?.model)
    ) {
      openThirdPartyEdit(device.uuid, device.type === 'camera')
    } else {
      setServiceNoteOpen(true)
    }
  }

  const handleCameraNoticeEdit = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setCameraNoticeEditing(true)
  }

  const handleCameraNoticeCancel = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setCameraNotice(device.notice || '')
    setCameraNoticeEditing(false)
  }

  const handleCameraNoticeSave = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setCameraNoticeSaving(true)

    dispatch(
      updateCameraNotice({
        deviceUuid: device.uuid,
        notice: cameraNotice.trim(),
      })
    )
      .then(unwrapResult)
      .then(({ notice }) => {
        setCameraNotice(notice || '')
        setCameraNoticeEditing(false)
        dispatch(showInfoMessage(t('devices:camera_notice.saved')))
      })
      .catch((error) => {
        dispatch(
          showErrorMessage(error || t('devices:camera_notice.save_error'))
        )
      })
      .finally(() => setCameraNoticeSaving(false))
  }

  return (
    <>
      <DropList
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={isMenuOpen}
        close={handleToggleMenu}
        anchorEl={anchorEl?.current}
      >
        {isCamera && (
          <DropListItem
            label={t('devices:cameras.unlink')}
            onClick={() => disconnectDevice(device)}
          />
        )}
        {!isEltexDevice && (
          <DropListItem
            label={t('devices:actions.download_archive')}
            onClick={() => selectArchiveDevice(device.uuid, isCamera, true)}
          />
        )}
      </DropList>
      <DeviceServiceNoteModal
        open={isServiceNoteOpen}
        device={device}
        onClose={() => setServiceNoteOpen(false)}
      />
      <DeviceCard {...props}>
        {/* Ширина левого блока не зависит от длины заметки: он делит строку
            с иконками архива и меню, пока ему остаётся от 320px, иначе иконки
            уходят вниз (как на телефоне); текст стоит рядом с иконкой
            устройства, пока ему остаётся от 240px. */}
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px 0px',
            cursor: 'pointer',
            flex: '1 1 320px',
            minWidth: 0,
          }}
        >
          <IconWrapper style={{ marginRight: 16, height: 40, width: 40 }}>
            {isCamera ? (
              <Camera color={IconColors.white} />
            ) : (
              <Intercom color={IconColors.white} />
            )}
          </IconWrapper>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            {/* заметка, которой не хватает места, переносится на свою строку */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '4px 7px',
              }}
            >
              <Typography>
                {device && !isCamera
                  ? formatDeviceId(device?.serial_number)
                  : device?.serial_number}
              </Typography>
              <StatusDeviceIndicator online={device.is_online} />
              <DeviceModel model={device.model} />
              <ThirdPartyEditIcon
                alias={device?.alias}
                t={t}
                onClick={handleEditServiceNote}
              />
            </div>
            <Typography variant="note" color="textSecondary">
              {device.short_address}
            </Typography>
            {device.notice_editable && !isCameraNoticeEditing && (
              <CameraNoticePreview onClick={(event) => event.stopPropagation()}>
                {device.notice && (
                  <Typography variant="note" color="textSecondary">
                    {t('devices:camera_notice.label')}: {device.notice}
                  </Typography>
                )}
                <Button
                  variant="v2"
                  tertiary
                  style={{ width: 'fit-content', padding: 0 }}
                  onClick={handleCameraNoticeEdit}
                >
                  {t(
                    device.notice
                      ? 'devices:camera_notice.edit'
                      : 'devices:camera_notice.add'
                  )}
                </Button>
              </CameraNoticePreview>
            )}
          </div>
        </Box>
        {/* архив и меню переносятся только вместе и стоят по краям своего
            блока: на отдельной строке — как раньше на телефоне, рядом с
            левым блоком делят с ним свободное место */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flex: '1 0 auto',
          }}
        >
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {!isEltexDevice &&
              deviceServices?.map((identifier) => (
                <IconButton
                  key={identifier}
                  onClick={() =>
                    selectArchiveDepth(device.uuid, null, isCamera, true)
                  }
                  style={{
                    padding: '4px',
                    marginLeft: '0',
                  }}
                >
                  <Tooltip title="Change archive depth">
                    {archive_icons[identifier]}
                  </Tooltip>
                </IconButton>
              ))}
          </div>
          {!isEltexDevice && (
            <MoreOptions
              style={{ cursor: 'pointer', marginLeft: '16px' }}
              ref={anchorEl}
              onClick={handleToggleMenu}
            />
          )}
        </div>
        {device.notice_editable && isCameraNoticeEditing && (
          <CameraNoticeEditor onClick={(event) => event.stopPropagation()}>
            <CameraNoticeField
              value={cameraNotice}
              disabled={isCameraNoticeSaving}
              onChange={(event) => {
                if (event.target.value.length <= CAMERA_NOTICE_MAX_LENGTH) {
                  setCameraNotice(event.target.value)
                }
              }}
            />
            <CameraNoticeActions>
              <Button
                variant="v2"
                tertiary
                disabled={isCameraNoticeSaving}
                onClick={handleCameraNoticeCancel}
              >
                {t('common:button.cancel')}
              </Button>
              <Button
                variant="v2"
                primary
                rounded
                disabled={isCameraNoticeSaving}
                onClick={handleCameraNoticeSave}
              >
                {t('common:button.save')}
              </Button>
            </CameraNoticeActions>
          </CameraNoticeEditor>
        )}
      </DeviceCard>
    </>
  )
}
